import React, { Suspense, lazy, useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Layout from './components/Layout';
import api from './services/api';
import { rememberReferral } from './utils/referral';

// Split at the route. The landing page is what most visitors load and it has
// no reason to carry the dashboard, the article reader or the auth forms.
const Landing = lazy(() => import('./pages/Landing'));
const Magazine = lazy(() => import('./pages/Magazine'));
const ArticleDetail = lazy(() => import('./pages/ArticleDetail'));
const InstallPage = lazy(() => import('./pages/Install'));
const LegalPage = lazy(() => import('./pages/Legal'));
const Login = lazy(() => import('./pages/Login'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const NotFound = lazy(() => import('./pages/NotFound'));

function ProtectedRoute({ children }) {
  const token = localStorage.getItem('token');
  const location = useLocation();
  if (!token) {
    // Carries where they were going, so signing in returns them to it rather
    // than to the landing page.
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  return children;
}

/** A new page starts at the top, the way a new page does. */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [pathname]);
  return null;
}

/** Shown while a route's chunk arrives. Sized to the page so nothing jumps. */
function RouteFallback() {
  return (
    <div className="page section" aria-busy="true">
      <div className="stack">
        <div className="skeleton" style={{ height: 48, maxWidth: 420 }} />
        <div className="skeleton" style={{ height: 20, maxWidth: 560 }} />
        <div className="skeleton" style={{ height: 320, borderRadius: 'var(--radius-lg)' }} />
      </div>
    </div>
  );
}

function RedirectToPlay() {
  useEffect(() => {
    window.location.replace('/play/' + window.location.search);
  }, []);
  return <RouteFallback />;
}

/**
 * Asks the server which language to open in, once.
 *
 * Only when the visitor has not chosen one themselves: a returning visitor's
 * choice outranks a guess made from their IP.
 */
function useServerLocale() {
  const { i18n } = useTranslation();

  useEffect(() => {
    if (localStorage.getItem('i18nextLng')) return;

    let cancelled = false;
    api
      .get('/system/locale')
      .then((response) => {
        if (cancelled) return;
        const locale = response.data?.locale;
        if (locale) i18n.changeLanguage(locale);
      })
      .catch(() => {
        // The default language is already applied; a failed guess is not worth
        // reporting to the visitor.
      });

    return () => {
      cancelled = true;
    };
  }, [i18n]);
}

function AppRoutes() {
  useServerLocale();
  const location = useLocation();

  // The agent's code, taken off whatever page the link happened to land on.
  //
  // Read on every navigation rather than once on the sign-up page: an invite
  // link points at the landing page as often as at the form, and somebody who
  // arrives, reads, and registers two screens later still has to carry the
  // code with them. See utils/referral.js.
  useEffect(() => {
    rememberReferral(location.search);
  }, [location.search]);

  return (
    <>
      <ScrollToTop />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/magazine" element={<Magazine />} />
          <Route path="/magazine/:id" element={<ArticleDetail />} />
          <Route path="/install" element={<InstallPage />} />

          {/* One component, three pages: each reads its own field out of the
              `legal` section the admin edits. */}
          <Route path="/terms" element={<LegalPage docKey="terms" />} />
          <Route path="/privacy" element={<LegalPage docKey="privacy" />} />
          <Route path="/about" element={<LegalPage docKey="about" />} />

          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<RedirectToPlay />} />

          {/* Invite links already in the wild.
            *
            * `/api/wallet/referral` has been handing agents
            * `${domain}/join?ref=CODE` and this site has never had a `/join`
            * route, so every one of those links went to the 404 page. The
            * builder is fixed, but the links an agent has already sent are in
            * other people's messages and cannot be recalled - so the path stays,
            * and forwards. The query goes with it, which is what carries the
            * code. */}
          <Route path="/join" element={<RedirectToPlay />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />

          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </>
  );
}

export default function App() {
  return (
    <Router>
      <Layout>
        <AppRoutes />
      </Layout>
    </Router>
  );
}
